import { ApplicationContext, PreferencesContext } from '../App';
import { LayoutContent, KeyboardTrigger, KeyboardContext } from './Layout';
import { WrappedInput } from './Settings';
import Button from './PendantButton';
import { TabOption } from './Sidebar';
import {
	COLOR,
	isWebDeployment,
	wrapChangeEventString,
	onSelectedUserChanged,
} from './shared/shared';
import { Language, UnitsSystem, Application, UserProfiles } from 'libapi';
import React, { useContext, useEffect, useState, useRef } from 'react';
import Modal, { Unlock } from './Modal';

import {
	Scroll,
	fetchCoreAppConfig,
	fetchMachineConfig,
	useCoreAppConfig,
	useMachineConfig,
} from './General';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { ConfirmationModal } from './Settings';
import { MigrationModal } from './MigrationModal';
import {
	diffArrayWithProto,
	diffConfigs,
	mergeAndPrune,
	mergeArrayWithProto,
	MigrationDiff,
	isObj,
	stripAndNormalizeComplete,
	combineDiffs,
	mergeFieldArray,
	dedupDiff,
} from './helpers/config-migrate';

function toHumanString(v: string) {
	return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
}

interface AppSettingsProps {
	SHOW_REDUCED: boolean;
}

interface UserSelectionModalProps {
	t1: string;
	bCancel: string;
	bConfirm: string;
	setShowUserSelect: (pShow: boolean) => void;
	showVentionUser: boolean;
}

export const UserSelectionModal: React.FC<UserSelectionModalProps> = ({
	t1,
	bCancel,
	bConfirm,
	setShowUserSelect,
	showVentionUser,
}) => {
	const { preferences, setPreferences } = useContext(PreferencesContext) as any;
	const lKeyboardTrigger: KeyboardTrigger = useContext(KeyboardContext);

	const modalRef = useRef<HTMLDivElement>(null);

	const userMap: Map<string, UserProfiles> = preferences.users;

	const defaultUser: UserProfiles = {
		name: 'Default User',
		access_level: 0,
		disabled_pages: ['Settings', 'Calibration', 'ControlCenter', 'Configuration'],
	};
	// Get the selected user based on the active user name
	const [selectedUser, setSelectedUser] = useState<UserProfiles>(() => {
		let firstNonActiveUser = defaultUser;
		for (let [key, value] of preferences.users) {
			if (key !== preferences.active_user_name) {
				firstNonActiveUser = value;
				break;
			}
		}
		return firstNonActiveUser;
	});
	const [savedPassword, setSavedPassword] = useState<string | undefined>(
		undefined
	);
	const [passwordInput, setPasswordInput] = useState<string | undefined>(
		undefined
	);
	const [confirmMessage, setConfirmMessage] = useState<string>('');
	const [confirmMessageColour, setConfirmMessageColour] =
		useState<string>('grey');

	useEffect(() => {
		setSavedPassword(selectedUser.password);
	}, [selectedUser]);

	useEffect(() => {
		showConfirmation(``, '');
	}, [passwordInput]);

	const showConfirmation = (message: string, type: string) => {
		if (type === 'error') {
			setConfirmMessageColour('red');
		} else if (type === 'success') {
			setConfirmMessageColour('green');
		} else {
			setConfirmMessageColour('grey');
		}
		setConfirmMessage(message);
	};

	const renderUserSelected = (): JSX.Element[] => {
		const options: JSX.Element[] = [];
		preferences.users.forEach((user: UserProfiles) => {
			if (
				user.name !== preferences.active_user_name && // Conditions to show only inactive Users
				(user.name !== 'Vention' || // Conditions to show Vention User
					showVentionUser)
			) {
				options.push(
					<option key={user.name} value={user.name}>
						{toHumanString(user.name)}
					</option>
				);
			}
		});
		return options;
	};

	const handleConfirmPassword = (userName: string, pass: string | undefined) => {
		const user = userMap.get(userName) || defaultUser;
		const savedPass = user?.password || undefined;

		if (pass === savedPass) {
			const p = { ...preferences, active_user_name: userName };
			showConfirmation(`Updating User Level to ${userName}...`, 'success');
			setTimeout(() => {
				setPreferences(p);
			}, 1500);
		} else {
			showConfirmation(`Wrong Password for ${userName}`, 'error');
		}
	};

	return (
		<Modal close={() => setShowUserSelect(false)}>
			<div
				ref={modalRef}
				className="UserPassword height-40 width-30 double-padding flex-col flex-between"
			>
				<span className="font-5 color-blue text-align-center">{t1}</span>
				{selectedUser.name && (
					<select
						className="StyledSelect"
						value={selectedUser.name}
						onChange={onSelectedUserChanged(
							preferences,
							setSelectedUser,
							setSavedPassword
						)}
					>
						{renderUserSelected()}
					</select>
				)}
				{savedPassword === undefined && (
					<span className="font-2 color-blue text-align-center">No Password</span>
				)}
				{!(savedPassword === undefined) && (
					<div className="flex-col">
						<span className="font-2 color-blue text-align-center">Password</span>
						<WrappedInput
							inputType={'password'}
							field={`password for ${selectedUser.name}`}
							value={passwordInput ? '********' : ''}
							unitsSystem={void 0}
							setter={pNewValue => setPasswordInput(String(pNewValue))}
							triggerKeyboard={lKeyboardTrigger}
						/>
					</div>
				)}
				<div className="flex-row flex-between">
					<Button
						className="font-2 double-padding vw-12"
						pAction={() => setShowUserSelect(false)}
					>
						{bCancel}
					</Button>
					<Button
						className="font-2 double-padding vw-12"
						pAction={() => {
							handleConfirmPassword(selectedUser.name, passwordInput);
						}}
						pNormalColorOverride={COLOR.LIGHT_BLUE}
					>
						{bConfirm}
					</Button>
				</div>
				{confirmMessage && (
					<span
						className="font-2 color-blue text-align-center"
						style={{ color: confirmMessageColour }}
					>
						{confirmMessage}
					</span>
				)}
				<span className="font-1 color-blue text-align-center">
					Current Active User: {preferences.active_user_name || ''}
				</span>
			</div>
		</Modal>
	);
};

export function AppSettings({ SHOW_REDUCED }: AppSettingsProps) {
	const { preferences, setPreferences } = useContext(PreferencesContext) as any;
	const app = useContext(ApplicationContext) as Application;

	const [localPreferences, setLocalPreferences] = useState({ ...preferences });
	const [lAllOptions, lSetAllOptions] = useState<{
		[key: string]: boolean | string | number;
	}>({});

	const userMap: Map<string, UserProfiles> = preferences.users;

	const defaultUser: UserProfiles = {
		name: 'Default User',
		access_level: 0,
		disabled_pages: ['Settings', 'Calibration', 'ControlCenter', 'Configuration'],
	};

	const defaultUserName = 'Operator'; // Replace with the specific UserName you want to set as backup when the active user should be hidden
	const hiddenUserName = 'Vention'; // Replace with the specific UserName you want to hide
	const activeUser = userMap.get(preferences.active_user_name) || defaultUser;
	const initialUser =
		userMap.get(
			preferences.active_user_name === hiddenUserName
				? defaultUserName
				: activeUser.name
		) || defaultUser;
	const [selectedUser, setSelectedUser] = useState<UserProfiles>(initialUser);
	const [savedPassword, setSavedPassword] = useState<string | undefined>(
		initialUser.password
	);
	const [oldPasswordInput, setOldPasswordInput] = useState<string | undefined>(
		undefined
	);
	const [passwordInput, setPasswordInput] = useState<string | undefined>(
		undefined
	);
	const [confirmedPasswordInput, setConfirmedPasswordInput] = useState<
		string | undefined
	>(undefined);
	const [isValidatedPassword, setIsValidatedPassword] = useState<boolean>(false);
	const [isUpdateSettingsOpen, setIsUpdateSettingsOpen] =
		useState<boolean>(false);
	const [confirmMessage, setConfirmMessage] = useState<string>('');
	const [confirmMessageColour, setConfirmMessageColour] = useState<string>('');
	const [showFactoryReset, setShowFactoryReset] = useState<boolean>(false);
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [pendingMigrations, setPendingMigrations] = useState<Record<
		string,
		MigrationDiff
	> | null>(null);
	const [showMigrationModal, setShowMigrationModal] = useState(false);
	const confirmUploadRef = useRef<(() => Promise<void>) | null>(null);

	const lKeyboardTrigger: KeyboardTrigger = useContext(KeyboardContext);
	type Baselines = Record<string, any>;

	useEffect(function () {
		if (!isWebDeployment()) {
			app.api.Generic.get('preferences', 'all')
				.then(function (pData) {
					lSetAllOptions(pData || {});
				})
				.catch(console.log);
		}
	}, []);

	useEffect(() => {
		validatePasswords(
			selectedUser.name,
			passwordInput,
			confirmedPasswordInput,
			oldPasswordInput,
			savedPassword
		);
	}, [lKeyboardTrigger]);

	/* Preference: units system */
	const renderUnitsSystems = () => {
		let r = [];
		for (const v in UnitsSystem) {
			const k = UnitsSystem[v as keyof typeof UnitsSystem];
			r.push(
				<option key={k} value={k}>
					{toHumanString(v)}
				</option>
			);
		}
		return r;
	};

	const onUnitsSystemChanged = () =>
		wrapChangeEventString((us: String) => {
			let p = { ...preferences };
			p.units_system = us; // UnitsSystem[us as keyof typeof UnitsSystem]};
			setPreferences(p);
		});

	/* Preference: language */
	const renderLanguage = () => {
		let r = [];
		for (const v in Language) {
			const k = Language[v as keyof typeof Language];
			if (isWebDeployment() && k !== Language.ENGLISH) {
				r.push(
					<option key={k} value={k} className='disabled'>
						{toHumanString(v)}
					</option>
				);
				continue;
			}
			r.push(
				<option key={k} value={k}>
					{toHumanString(v)}
				</option>
			);
		}
		return r;
	};

	const onLanguageChanged = () =>
		wrapChangeEventString((lang: string) => {
			// Update preferences in state
			let p = { ...preferences };
			p.language = lang;
			setPreferences(p);
		});

	/* Preference: user level access */
	const renderUserToUpdate = (): JSX.Element[] => {
		const options: JSX.Element[] = [];
		preferences.users.forEach((user: UserProfiles) => {
			if (user.name !== 'Vention') {
				options.push(
					<option key={user.name} value={user.name}>
						{toHumanString(user.name)}
					</option>
				);
			}
		});
		return options;
	};

	const onDisabledPagesChanged =
		(userName: string, page: string) =>
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const updatedUsers = new Map<string, UserProfiles>();

			localPreferences.users.forEach((user: UserProfiles, key: string) => {
				if (user.name === userName) {
					const disabledPages = new Set(user.disabled_pages);
					if (event.target.checked) {
						disabledPages.add(page);
					} else {
						disabledPages.delete(page);
					}
					updatedUsers.set(key, {
						...user,
						disabled_pages: Array.from(disabledPages),
					});
				} else {
					updatedUsers.set(key, user);
				}
			});

			setLocalPreferences({
				...preferences,
				users: updatedUsers,
			});
		};

	/* Preference: robot configurations */
	// TODO: suggest the contents when nothing
	// TODO: validation? use ace?
	const onRobotConfigurationsChanged = () =>
		wrapChangeEventString((cfg: String) => {
			if (isWebDeployment()) return;
			let p = { ...preferences };
			p.robot_configurations = cfg;
			setPreferences(p);
			if (!isWebDeployment()) {
				app.api.Generic.set(['preferences'], p);
			} else {
			}
		});

	const lHandleAllChange = (pKey: string) => (pValue: any) => {
		const _lCp = { ...lAllOptions };
		_lCp[pKey] = pValue;
		lSetAllOptions(_lCp);
		if (!isWebDeployment()) {
			app.api.Generic.set(['preferences', 'all'], _lCp).catch(console.log);
		} else {
		}
	};

	const showConfirmation = (message: string, type: string) => {
		if (type === 'error') {
			setConfirmMessageColour('red');
		} else if (type === 'success') {
			setConfirmMessageColour('green');
		} else {
			setConfirmMessageColour('grey');
		}
		setConfirmMessage(message);
	};

	const saveUserSettings = () => {
		const password = userMap.get(selectedUser.name)?.password;
		const disabledPages = userMap.get(selectedUser.name)?.disabled_pages;
		const localPassword = localPreferences.users.get(selectedUser.name)?.password;
		const localDisabledPages = localPreferences.users.get(
			selectedUser.name
		)?.disabled_pages;
		if (password === localPassword && disabledPages === localDisabledPages) {
			showConfirmation(`No Changes Made for ${selectedUser.name}`, 'error');
			return;
		} else if (password !== localPassword) {
			// Password has been changed
			if (!isValidatedPassword) {
				showConfirmation(`Old Password does not match`, 'error');
				return;
			}
			if (disabledPages === localDisabledPages) {
				// Disabled Elements have NOT been changed
				showConfirmation(
					`Updating Password for ${selectedUser.name}...`,
					'success'
				);
				setTimeout(() => {
					showConfirmation(``, '');
					setPreferences(localPreferences);
				}, 1500);
			} else {
				// Disabled Elements have been changed
				showConfirmation(
					`Updating Password and Accessibility for ${selectedUser.name}...`,
					'success'
				);
				setTimeout(() => {
					showConfirmation(``, '');
					setPreferences(localPreferences);
				}, 1500);
			}
		} else {
			// Password has NOT been changed
			showConfirmation(
				// Disabled Elements have been changed
				`Updating Accessibility for ${selectedUser.name}...`,
				'success'
			);
			setTimeout(() => {
				showConfirmation(``, '');
				setPreferences(localPreferences);
			}, 1500);
		}
	};

	const validatePasswords = (
		selectedUserName: string, // Add logic for higher user levels to have no old password
		passwordInput?: string,
		confirmedPasswordInput?: string,
		oldPasswordInput?: string,
		savedPassword?: string
	) => {
		if (passwordInput && confirmedPasswordInput) {
			if (
				oldPasswordInput !== savedPassword &&
				activeUser.access_level < 100 &&
				selectedUser.access_level >= activeUser.access_level
			) {
				setIsValidatedPassword(false);
			} else {
				setIsValidatedPassword(true);
			}
			if (passwordInput !== confirmedPasswordInput) {
				showConfirmation(`New Passwords do not match`, 'error');
				return;
			}
			const updatedUsers = new Map<string, UserProfiles>();

			localPreferences.users.forEach((user: UserProfiles, key: string) => {
				if (user.name === selectedUserName) {
					updatedUsers.set(key, { ...user, password: passwordInput });
				} else {
					updatedUsers.set(key, user);
				}
			});
			showConfirmation(``, '');
			setLocalPreferences({
				...preferences,
				users: updatedUsers,
			});
		}
	};

	// Function not compatible with Web Deployment
	async function downloadConfiguration() {
		var zip = new JSZip();

		const ac = await fetchCoreAppConfig(app);
		const mc = await fetchMachineConfig(app);
		const pc = await app.api.Pallet.get();
		const vrdf = await app.api.VRDFConfig.get();
		const ext = await app.api.CoreIOConfig.get();
		const gripper = await app.api.GripperConfig.get();
		const generic = await app.api.Generic.get();
		const sku = await app.api.SKUSpecific.get();
		const files = {
			app_config: ac,
			machineConfig: mc,
			palletConfigs: pc,
			vrdf_config: vrdf,
			ext_config: ext,
			grippers: gripper,
			genericData: generic,
			skuSpecific: sku,
		};

		// Loop through the JSON files object
		for (const [filename, jsonData] of Object.entries(files)) {
			zip.file(filename + '.json', JSON.stringify(jsonData, null, 2));
		}

		// Fetch the core logs ZIP
		const logsResponse = await fetch('/api/core-logs');
		if (logsResponse.ok) {
			// Read the response as a Blob (binary data)
			const logsBlob = await logsResponse.blob();
			// Use JSZip to load the ZIP blob
			const logsZip = await JSZip.loadAsync(logsBlob);

			// Add each file in the logs ZIP to the main ZIP
			for (const [filename, file] of Object.entries(logsZip.files)) {
				const fileData = await file.async('blob');
				zip.file(filename, fileData);
			}
		} else {
			console.error('Failed to fetch logs:', logsResponse.statusText);
		}

		// Generate the ZIP file
		zip.generateAsync({ type: 'blob' }).then(function (content) {
			saveAs(content, 'palletizer.zip');
		});
	}

	async function factoryReset() {
		try {
			await app.api.Config.factoryReset();
			app.restartCore();
			window.location.href = '/';
		} catch (e) {
			console.error(e);
		}
	}

	const uploadConfigs = async (files: FileList) => {
		const fileArray = Array.from(files);
		setIsUploading(true);
		setConfirmMessage('');
		setConfirmMessageColour('grey');

		const allowed = [
			'app_config.json',
			'ext_config.json',
			'grippers.json',
			'skuSpecific.json',
			'genericData.json',
			'machineConfig.json',
			'palletConfigs.json',
		] as const;

		// ------------------------------------------------------------------
		// 0. fetch baselines
		// ------------------------------------------------------------------
		let baselines: Baselines;
		try {
			baselines = await app.api.Config.getBaselineConfigs(); // already parsed
		} catch (e: any) {
			showConfirmation(`Could not fetch baselines: ${e.message}`, 'error');
			setIsUploading(false);
			return;
		}
		// ------------------------------------------------------------------
		// 1. collect valid files (handle .zip)
		// ------------------------------------------------------------------
		let toUpload: File[] = [];
		if (fileArray.length === 1 && fileArray[0].name.endsWith('.zip')) {
			try {
				const zip = await JSZip.loadAsync(fileArray[0]);
				for (const p of Object.keys(zip.files)) {
					if (allowed.some(name => p.endsWith(name))) {
						const blob = await zip.files[p].async('blob');
						toUpload.push(new File([blob], p, { type: 'application/json' }));
					}
				}
				if (!toUpload.length) throw new Error('No valid config files in ZIP');
			} catch (e: any) {
				console.log('Failed to unpack ZIP:', e);
				showConfirmation(`Failed to unpack ZIP: ${e.message}`, 'error');
				setIsUploading(false);
				return;
			}
		} else {
			toUpload = Array.from(fileArray).filter(f =>
				allowed.some(name => f.name.endsWith(name))
			);
			if (!toUpload.length) {
				console.log('No valid config files selected');
				showConfirmation('No valid JSON config files selected', 'error');
				setIsUploading(false);
				return;
			}
		}

		// ------------------------------------------------------------------
		// 2. diff / merge locally
		// ------------------------------------------------------------------
		const diffs: Record<string, MigrationDiff> = {};
		const mergedBlobs: [string, Blob][] = [];

		for (const file of toUpload) {
			const uploadedObj = JSON.parse(await file.text());
			const defaultObj = baselines[file.name];

			if (!defaultObj) {
				showConfirmation(`No baseline found for ${file.name}`, 'error');
				setIsUploading(false);
				return;
			}

			let merged: any = uploadedObj; // will replace per special case
			let fileDiff: MigrationDiff = { additions: [], removals: [] };

			switch (file.name) {
				// ───────────────────────── grippers.json ─────────────────────────
				case 'grippers.json': {
					// defaultObj & uploadedObj are maps: name → gripper‑config
					const proto = Object.values(defaultObj)[0] ?? {}; // first default gripper
					const mergedMap: Record<string, any> = {};

					for (const [name, up] of Object.entries(uploadedObj)) {
						const base = isObj(defaultObj[name]) ? defaultObj[name] : proto;

						const d = diffConfigs(base, up, name);
						fileDiff.additions.push(...d.additions);
						fileDiff.removals.push(...d.removals);

						mergedMap[name] = mergeAndPrune(base, up);
					}
					merged = mergedMap;
					break;
				}

				// ─────────────────────── palletConfigs.json ──────────────────────
				case 'palletConfigs.json': {
					const defCfg = defaultObj.config ?? {};
					const upCfg = uploadedObj.config ?? {};

					const mergedCfg: any = { ...upCfg };
					let diffTotal: MigrationDiff = { additions: [], removals: [] };

					// ---------- boxes -------------------------------------------------
					if (
						Array.isArray(upCfg.boxes) &&
						Array.isArray(defCfg.boxes) &&
						defCfg.boxes.length
					) {
						const boxProto = defCfg.boxes[0];
						diffTotal = combineDiffs(
							diffTotal,
							diffArrayWithProto(boxProto, upCfg.boxes, 'config.boxes')
						);
						mergedCfg.boxes = mergeArrayWithProto(boxProto, upCfg.boxes);
					}

					// ---------- slipsheets -------------------------------------------
					if (
						Array.isArray(upCfg.slipsheets) &&
						Array.isArray(defCfg.slipsheets) &&
						defCfg.slipsheets.length
					) {
						const slipProto = defCfg.slipsheets[0];
						diffTotal = combineDiffs(
							diffTotal,
							diffArrayWithProto(slipProto, upCfg.slipsheets, 'config.slipsheets')
						);
						mergedCfg.slipsheets = mergeArrayWithProto(slipProto, upCfg.slipsheets);
					}

					// ---------- pallets (+ Layouts, Stack, boxPositions) --------------
					if (
						Array.isArray(upCfg.pallets) &&
						Array.isArray(defCfg.pallets) &&
						defCfg.pallets.length
					) {
						const palProto = defCfg.pallets[0];

						const mergedPallets = upCfg.pallets.map((pal: any, pIdx: number) => {
							const basePal = isObj(defCfg.pallets[pIdx])
								? defCfg.pallets[pIdx]
								: palProto;
							const palPath = `config.pallets[${pIdx}]`;

							// Layouts & their boxPositions
							if (
								Array.isArray(pal.Layouts) &&
								Array.isArray(basePal.Layouts) &&
								basePal.Layouts.length
							) {
								const layProto = basePal.Layouts[0];

								pal.Layouts = pal.Layouts.map((lay: any, lIdx: number) => {
									const baseLay = isObj(basePal.Layouts[lIdx])
										? basePal.Layouts[lIdx]
										: layProto;
									const layPath = `${palPath}.Layouts[${lIdx}]`;

									// merge / diff boxPositions inside each layout
									mergeFieldArray(
										lay,
										'boxPositions',
										baseLay.boxPositions ?? [],
										`${layPath}.boxPositions`,
										diffTotal
									);

									// diff / merge layout object itself
									const ld = diffConfigs(baseLay, lay, layPath);
									diffTotal.additions.push(...ld.additions);
									diffTotal.removals.push(...ld.removals);

									return mergeAndPrune(baseLay, lay);
								});
							}

							// Stack array
							if (
								Array.isArray(pal.Stack) &&
								Array.isArray(basePal.Stack) &&
								basePal.Stack.length
							) {
								const stackProto = basePal.Stack[0];
								pal.Stack = mergeArrayWithProto(stackProto, pal.Stack);
								diffTotal = combineDiffs(
									diffTotal,
									diffArrayWithProto(stackProto, pal.Stack, `${palPath}.Stack`)
								);
							}

							// diff / merge pallet object itself
							const pd = diffConfigs(basePal, pal, palPath);
							diffTotal.additions.push(...pd.additions);
							diffTotal.removals.push(...pd.removals);

							return mergeAndPrune(basePal, pal);
						});

						mergedCfg.pallets = mergedPallets;
					}

					// ---------- assemble & sanitise -----------------------------------
					merged = { ...uploadedObj, config: mergedCfg };
					delete (merged as any).complete; // remove root flag
					stripAndNormalizeComplete(merged); // set nested .complete = false
					fileDiff = diffTotal;
					break;
				}
				// ────────────────────────── skuSpecific.json ─────────────────────
				case 'skuSpecific.json': {
					mergedBlobs.push([
						file.name,
						new Blob([await file.text()], { type: 'application/json' }),
					]);
					continue; // skip diff / merge logic
				}

				// ───────────────────────── generic files ────────────────────────
				default: {
					fileDiff = diffConfigs(defaultObj, uploadedObj);
					merged = mergeAndPrune(defaultObj, uploadedObj);
				}
			}

			// collect diffs & merged blobs
			fileDiff = dedupDiff(fileDiff);
			if (fileDiff.additions.length || fileDiff.removals.length) {
				diffs[file.name] = fileDiff;
			}
			mergedBlobs.push([
				file.name,
				new Blob([JSON.stringify(merged, null, 2)], { type: 'application/json' }),
			]);
		}

		// ------------------------------------------------------------------
		// 3. prepare final POST helper
		// ------------------------------------------------------------------
		const runUpload = async () => {
			const form = new FormData();
			mergedBlobs.forEach(([name, blob]) => form.append('configs', blob, name));

			try {
				const res = await fetch('/api/upload-configs', {
					method: 'POST',
					body: form,
				});
				if (!res.ok) throw new Error(await res.text());
				showConfirmation(await res.text(), 'success');
			} catch (e: any) {
				console.error(e);
				showConfirmation(`Save failed: ${e.message}`, 'error');
			} finally {
				setIsUploading(false);
				app.restartCore();
				window.location.href = '/';
			}
		};

		// ------------------------------------------------------------------
		// 4. no diffs → upload immediately, else show modal
		// ------------------------------------------------------------------
		if (Object.keys(diffs).length === 0) {
			await runUpload();
			return;
		}

		// store callback for modal
		confirmUploadRef.current = runUpload;

		setPendingMigrations(diffs);
		setShowMigrationModal(true);
		// (title is hard‑coded in JSX render)
	};

	// Function to handle button click and trigger file input
	const handleButtonClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	// Function to handle file selection
	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (files && files.length > 0) {
			uploadConfigs(files);
			// Reset the input value to allow uploading the same file again if needed
			e.target.value = '';
		}
	};

	return (
		<>
			<Scroll className="full-width flex-col align-center">
				<div className="full-size flex-col align-center">
					<div className="flex-row full-width flex-between align-center vh-10 padding-top padding-bottom border-bottom">
						<div className="flex-row full-height align-center">
							<span className="double-margin-left double-margin-top font-5 bold-font">
								General
							</span>
							<div className="double-padding"></div>
						</div>
					</div>
					<div className="flex-row full-width vh-10 padding-top padding-bottom border-bottom">
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="flex-col width-20">
							<span className="font-5 margin-bottom bold-font color-blue">Units</span>

							<select
								className="StyledSelect"
								value={preferences.units_system}
								onChange={onUnitsSystemChanged()}
							>
								{renderUnitsSystems()}
							</select>
						</div>
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="flex-col width-20">
							<span className="font-5 margin-bottom bold-font color-blue">
								Language
							</span>

							<select
								className="StyledSelect"
								value={preferences.language}
								onChange={onLanguageChanged()}
							>
								{renderLanguage()}
							</select>
						</div>
						<div className="double-padding"></div>
					</div>
					{/* {selectedUser.name !== undefined && ( */}
					<div className="flex-row full-width padding-top padding-bottom border-bottom">
						<div className="double-padding"></div>
						<div className="double-padding"></div>
						<div className="flex-col width-85 align-left double-margin-bottom font-2">
							<span className="font-5 margin-bottom bold-font color-blue padding-top">
								User Passwords and Accessibility
							</span>
							<div className="flex-row">
								{selectedUser.name && (
									<div className="flex-col width-24">
										<select
											className="StyledSelect"
											value={selectedUser.name}
											onChange={onSelectedUserChanged(
												preferences,
												setSelectedUser,
												setSavedPassword,
												setOldPasswordInput,
												setPasswordInput,
												setConfirmedPasswordInput
											)}
										>
											{renderUserToUpdate()}
										</select>
									</div>
								)}
								<div className="double-padding"></div>
								<div className="double-padding"></div>
								<div className="double-padding"></div>
								<div className="double-padding"></div>
								<div className="double-padding"></div>
								<div className="double-padding"></div>
								<div className="double-padding"></div>
								<div className="double-padding"></div>
								{selectedUser.name !== undefined && !isUpdateSettingsOpen && (
									<Button
										className="vw-16"
										pAction={() => {
											setIsUpdateSettingsOpen(true);
										}}
									>
										Change User Settings
									</Button>
								)}
								{isUpdateSettingsOpen && (
									<Button
										pNormalColorOverride={COLOR.MEDIUM_BLUE}
										className="vw-16"
										pAction={() => {
											setLocalPreferences(preferences);
											setIsUpdateSettingsOpen(false);
											showConfirmation(``, '');
										}}
									>
										Cancel
									</Button>
								)}
							</div>
							{isUpdateSettingsOpen && (
								<div className="flex-col align-center">
									<div className="double-padding"></div>
									<div className="flex-row width-80">
										{selectedUser.name === 'Operator' && (
											<div className="flex-col width-50 double-padding"></div>
										)}
										{selectedUser.name !== 'Operator' && (
											<div className="flex-col width-50 double-padding">
												{savedPassword === undefined && <span>No Password</span>}
												{!(savedPassword === undefined) &&
													selectedUser.access_level < activeUser.access_level && (
														<span>Old Password Not Needed</span>
													)}
												{!(
													savedPassword === undefined ||
													selectedUser.access_level < activeUser.access_level
												) && (
													<div>
														Old Password
														<WrappedInput
															inputType={'password'}
															field={`old password for ${selectedUser.name}`}
															value={oldPasswordInput ? '********' : ' '}
															unitsSystem={void 0}
															setter={pNewValue => setOldPasswordInput(String(pNewValue))}
															triggerKeyboard={lKeyboardTrigger}
														/>
													</div>
												)}
												<br />
												<br />
												New Password for {selectedUser.name}
												<WrappedInput
													inputType={'password'}
													field={`new password for ${selectedUser.name}`}
													value={passwordInput ? '********' : ' '}
													unitsSystem={void 0}
													setter={pNewValue => setPasswordInput(String(pNewValue))}
													triggerKeyboard={lKeyboardTrigger}
												/>
												<br />
												Confirm New Password
												<WrappedInput
													inputType={'password'}
													field={`new password again for ${selectedUser.name}`}
													value={confirmedPasswordInput ? '********' : ' '}
													unitsSystem={void 0}
													setter={pNewValue => setConfirmedPasswordInput(String(pNewValue))}
													triggerKeyboard={lKeyboardTrigger}
												/>
											</div>
										)}
										{selectedUser.access_level < activeUser.access_level && (
											<div className="flex-col width-30 double-padding">
												<div className="flex-row width-100 padding">
													<input
														type="checkbox"
														className="double-padding"
														onChange={onDisabledPagesChanged(
															selectedUser.name,
															'Configuration'
														)}
														checked={localPreferences.users
															.get(selectedUser.name)
															?.disabled_pages.includes('Configuration')}
														style={{ height: '20px', width: '20px' }}
													/>
													Disable Configuration
													<br />
												</div>
												<div className="flex-row width-100 padding">
													<input
														type="checkbox"
														className="padding"
														onChange={onDisabledPagesChanged(selectedUser.name, 'Settings')}
														checked={localPreferences.users
															.get(selectedUser.name)
															?.disabled_pages.includes('Settings')}
														style={{ height: '20px', width: '20px' }}
													/>
													Disable Settings <br />
												</div>
												<div className="flex-row width-100 padding">
													<input
														type="checkbox"
														className="padding"
														onChange={onDisabledPagesChanged(
															selectedUser.name,
															'Calibration'
														)}
														checked={localPreferences.users
															.get(selectedUser.name)
															?.disabled_pages.includes('Calibration')}
														style={{ height: '20px', width: '20px' }}
													/>
													Disable Calibration <br />
												</div>
												<div className="flex-row width-100 padding">
													<input
														type="checkbox"
														className="padding"
														onChange={onDisabledPagesChanged(
															selectedUser.name,
															'ControlCenter'
														)}
														checked={localPreferences.users
															.get(selectedUser.name)
															?.disabled_pages.includes('ControlCenter')}
														style={{ height: '20px', width: '20px' }}
													/>
													Disable Control Center{' '}
												</div>
											</div>
										)}
									</div>
									<div className="double-padding"></div>
									<div className="flex-col width-100 align-center">
										{confirmMessage && (
											<span
												className="font-2 color-blue text-align-center double-padding"
												style={{ color: confirmMessageColour }}
											>
												{confirmMessage}
											</span>
										)}
										<Button
											pNormalColorOverride={COLOR.LIGHT_BLUE}
											className="vw-16"
											pAction={() => {
												saveUserSettings();
											}}
										>
											Confirm
										</Button>
									</div>
								</div>
							)}
						</div>
					</div>
					{/* )} */}
					<div className="flex-row width-100 padding">
						<div className="double-padding" />
						<div className="double-padding" />
						<div className="flex-col width-20">
							<div className="double-padding"></div>
							<span className="font-5 margin-bottom bold-font color-blue">
								Download Configuration
							</span>

							<Button className="vw-20 font-2" pAction={downloadConfiguration} pDisabled={isWebDeployment()}>
								<span>Download Configuration and Logs</span>
							</Button>
							<span className="font-3 margin-top color-dark-grey">
								Download locally or to USB drive.
							</span>
						</div>
						<div className="double-padding" />
						<div className="double-padding" />
						<div className="double-padding" />
						<div className="double-padding" />
						<div className="double-padding" />
						<div className="double-padding" />
						<div className="double-padding" />
						<div className="double-padding" />
						<div className="flex-col width-20">
							<div className="double-padding"></div>
							<span className="font-5 margin-bottom bold-font color-blue">
								Factory Reset
							</span>

							<Button
								className="vw-20 font-2"
								pAction={() => setShowFactoryReset(true)}
								pDisabled={isWebDeployment()}							>
								<span>Reset all configs to their defaults</span>
							</Button>
						</div>
					</div>
					<div className="flex-row width-100 padding">
						<div className="double-padding" />
						<div className="double-padding" />
						<div className="flex-col width-20">
							<div className="double-padding"></div>
							<div className="double-padding"></div>
							<span className="font-5 margin-bottom bold-font color-blue">
								Upload Configs
							</span>
							{/* Hidden File Input */}
							<input
								type="file"
								multiple
								ref={fileInputRef}
								style={{ display: 'none' }}
								onChange={handleFileChange}
								accept=".json,.zip"
							/>
							{/* Single Upload Button */}
							<Button
								className="vw-20 font-2"
								pAction={handleButtonClick}
								pDisabled={isUploading || isWebDeployment()}
							>
								{isUploading ? 'Uploading...' : 'Upload Configs'}
							</Button>
							<span className="font-3 margin-top color-dark-grey">
								File names must end with an appropriate config name
							</span>
						</div>
					</div>
				</div>
			</Scroll>
			{showFactoryReset && (
				<ConfirmationModal
					t1={`Reset all settings to factory defaults?`}
					t2={'Are you sure?'}
					bCancel={'Cancel'}
					bConfirm={'Reset'}
					onConfirm={() => {
						setShowFactoryReset(false);
						factoryReset();
					}}
					onCancel={() => {
						setShowFactoryReset(false);
					}}
				/>
			)}
			{showMigrationModal && pendingMigrations && (
				<MigrationModal
					title="Warning: One or more of your configs are outdated"
					migrations={pendingMigrations}
					onCancel={() => {
						setShowMigrationModal(false);
						setIsUploading(false);
					}}
					onConfirm={async () => {
						setShowMigrationModal(false);
						setIsUploading(true);
						await confirmUploadRef.current?.(); // ← runUpload()
					}}
				/>
			)}
		</>
	);
}

export interface PreferencesProps {
	pTab: TabOption;
}

export default function Preferences({ pTab }: PreferencesProps) {
	// const handle_download = () => {
	// 	// For the web experience, download the json.
	// 	var downloadLink = document.createElement('a');
	// 	downloadLink.href = window.URL.createObjectURL(
	// 		new Blob([JSON.stringify(lCombinedConfig)], {
	// 			type: 'text/javascript',
	// 		})
	// 	);
	// 	downloadLink.download = 'settings.json';
	// 	downloadLink.click();
	// };
	const app = useContext(ApplicationContext) as Application;

	const { lMachineConfig } = useMachineConfig(app);
	const lCoreAppConfig = useCoreAppConfig(app);
	const IS_WEB = isWebDeployment();

	// const [SHOW_REDUCED, lSetShowReduced] = useState<boolean>(false);

	const [locked, setLocked] = useState<boolean>(true);

	const lMachineParametersIndex = 0;
	const lIOIndex = 1;
	const lGrippersIndex = 2;
	const lBehaviourIndex = 3;
	const lApplicationIndex = 4;
	const lSKUSpecificIndex = 5;

	const [lMenus, lSetMenus] = useState<string[]>([
		'Machine Parameters',
		'IO',
		'Grippers',
		'Box Path',
		'Application',
		'SKU Specific',
	]);
	const [lSelectedMenuIdx, lSetSelectedMenuIdx] = useState<number>(0);
	const [lEnabled, lSetEnabled] = useState<boolean[]>([
		true,
		true,
		true,
		true,
		true,
		true,
	]);

	useEffect(() => {
		// const red = !!(IS_WEB || lMachineConfig?.IsMaster);
		// lSetShowReduced(red);
		// [true, true, true, true, true, true];
	}, [lMachineConfig]);

	useEffect(() => {
		console.log('GOT CORE APP CONFIG:', lCoreAppConfig);
	}, [lCoreAppConfig]);

	return (
		<LayoutContent pTab={pTab} hideHeader={true}>
			<>
				{locked && (
					<Unlock
						close={() => {
							setLocked(false);
						}}
					></Unlock>
				)}
			</>
		</LayoutContent>
	);
}

// TODO: typesafe preferences
// TODO: font sizes
// TODO: upload - accept json
// TODO: upload - for each asset, determine if identical contents
// TODO: upload - if identical, keep; if not identical, determine if must rename; if must rename, do (along with all local referers)
